export const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''

let googleMapsLoadPromise = null
let placesLibraryPromise = null
let autocompleteSessionToken = null

export function hasGoogleMapsApiKey() {
  return !!GOOGLE_MAPS_API_KEY
}

function installGoogleMapsBootstrap() {
  if (window.google?.maps?.importLibrary) return

  /* eslint-disable */
  ;((g) => {
    let h
    let a
    let k
    const p = 'The Google Maps JavaScript API'
    const c = 'google'
    const l = 'importLibrary'
    const q = '__ib__'
    const m = document
    const b = window
    b[c] = b[c] || {}
    const d = b[c].maps || (b[c].maps = {})
    const r = new Set()
    const e = new URLSearchParams()
    const u = () =>
      h ||
      (h = new Promise(async (f, n) => {
        a = m.createElement('script')
        e.set('libraries', [...r] + '')
        for (k in g) {
          e.set(k.replace(/[A-Z]/g, (t) => `_${t[0].toLowerCase()}`), g[k])
        }
        e.set('callback', `${c}.maps.${q}`)
        a.src = `https://maps.googleapis.com/maps/api/js?${e}`
        d[q] = f
        a.onerror = () => n(new Error(`${p} could not load.`))
        a.nonce = m.querySelector('script[nonce]')?.nonce || ''
        m.head.append(a)
      }))
    d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n))
  })({ key: GOOGLE_MAPS_API_KEY, v: 'weekly' })
  /* eslint-enable */
}

/**
 * Loads the Maps JS bootstrap so importLibrary() works.
 */
export function loadGoogleMaps() {
  if (window.google?.maps?.importLibrary) {
    return Promise.resolve(window.google.maps)
  }
  if (googleMapsLoadPromise) return googleMapsLoadPromise

  googleMapsLoadPromise = new Promise((resolve, reject) => {
    if (!GOOGLE_MAPS_API_KEY) {
      reject(new Error('Missing VITE_GOOGLE_MAPS_API_KEY'))
      return
    }

    try {
      installGoogleMapsBootstrap()
      window.google.maps
        .importLibrary('places')
        .then(() => resolve(window.google.maps))
        .catch(reject)
    } catch (err) {
      reject(err)
    }
  })

  return googleMapsLoadPromise
}

export async function loadPlacesLibrary() {
  await loadGoogleMaps()
  if (!placesLibraryPromise) {
    placesLibraryPromise = window.google.maps.importLibrary('places')
  }
  return placesLibraryPromise
}

export async function getAutocompleteSessionToken() {
  const places = await loadPlacesLibrary()
  if (!autocompleteSessionToken) {
    autocompleteSessionToken = new places.AutocompleteSessionToken()
  }
  return autocompleteSessionToken
}

export function resetAutocompleteSessionToken() {
  autocompleteSessionToken = null
}

export function getPlacePhotoUrl(photo, maxWidth = 400) {
  if (!photo?.getURI && !photo?.getUrl) return null
  if (typeof photo.getURI === 'function') {
    return photo.getURI({ maxWidth })
  }
  return photo.getUrl({ maxWidth })
}

/** @deprecated use getPlacePhotoUrl */
export function getPlacePhoto(place, maxWidth = 400) {
  if (!place?.photos?.length) return null
  return getPlacePhotoUrl(place.photos[0], maxWidth)
}

export function formatVenueSearchError(err) {
  const message = String(err?.message || err || '')

  if (message.includes('Missing VITE_GOOGLE_MAPS_API_KEY')) {
    return 'Google Maps API key is missing. Add VITE_GOOGLE_MAPS_API_KEY to .env and restart the dev server.'
  }

  if (/RefererNotAllowed|ApiTargetBlocked|referer/i.test(message)) {
    return 'This API key is blocked on localhost. In Google Cloud → Credentials → your key → Application restrictions, add http://localhost:3000/* and http://127.0.0.1:3000/* — or set restrictions to None while testing locally.'
  }

  if (/ApiNotActivated|SERVICE_DISABLED|not enabled/i.test(message)) {
    return 'Enable Maps JavaScript API and Places API in Google Cloud → APIs & Services → Library, then restart the dev server.'
  }

  if (/InvalidKey|invalid.*key/i.test(message)) {
    return 'The Google Maps API key looks invalid. Copy a fresh key from Google Cloud → Credentials.'
  }

  if (message) {
    return `Venue search failed: ${message}`
  }

  return 'Venue search failed. Check the browser console for details, verify Places API (New) is enabled, and restart the dev server.'
}
